import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import UserAvatar from '../components/UserAvatar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, Camera, Loader2, Save, Lock, User as UserIcon, MapPin,
  MessageSquare, Phone, Mail, Trash2, Shield, ShieldCheck, Briefcase, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

const CONTACT_METHODS = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Phone Call', icon: Phone },
  { id: 'text', label: 'Text Message', icon: MessageSquare },
];

const ROLE_META = {
  master_admin: { label: 'Master Admin', icon: ShieldCheck, accent: 'text-red-600', tint: 'bg-red-50' },
  admin:        { label: 'Admin',        icon: Shield,      accent: 'text-red-600', tint: 'bg-red-50' },
  cleaner:      { label: 'Cleaner',      icon: Briefcase,   accent: 'text-blue-600', tint: 'bg-blue-50' },
  client:       { label: 'Client',       icon: Sparkles,    accent: 'text-emerald-600', tint: 'bg-emerald-50' },
};

const EMPTY = {
  full_name: '', phone: '', preferred_contact: 'email',
  address_line1: '', address_line2: '', city: '', state: '', zip: '', notes: '',
};

const AccountSettingsPage = () => {
  const { token, user: authUser, refreshUser, updateUserLocal } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/account/me`, { headers });
      setAccount(res.data);
      setForm({
        full_name: res.data.full_name || '',
        phone: res.data.phone || '',
        preferred_contact: res.data.preferred_contact || 'email',
        address_line1: res.data.address_line1 || '',
        address_line2: res.data.address_line2 || '',
        city: res.data.city || '',
        state: res.data.state || '',
        zip: res.data.zip || '',
        notes: res.data.notes || '',
      });
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const dirty = account && (
    form.full_name !== (account.full_name || '') ||
    form.phone !== (account.phone || '') ||
    form.preferred_contact !== (account.preferred_contact || 'email') ||
    form.address_line1 !== (account.address_line1 || '') ||
    form.address_line2 !== (account.address_line2 || '') ||
    form.city !== (account.city || '') ||
    form.state !== (account.state || '') ||
    form.zip !== (account.zip || '') ||
    form.notes !== (account.notes || '')
  );

  const save = async () => {
    if (!form.full_name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/account/me`, form, { headers });
      setAccount(res.data);
      updateUserLocal({
        full_name: res.data.full_name,
        phone: res.data.phone,
        photo_url: res.data.photo_url,
      });
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('Image must be under 2 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const res = await axios.post(`${API_URL}/api/account/me/avatar`,
          { data_url: reader.result }, { headers });
        setAccount(res.data);
        updateUserLocal({ photo_url: res.data.photo_url });
        await refreshUser();
        toast.success('Profile photo updated');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to upload photo');
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.onerror = () => toast.error('Could not read the image file');
    reader.readAsDataURL(file);
  };

  const removePhoto = async () => {
    if (!account?.photo_url) return;
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/account/me/avatar`, { headers });
      setAccount(res.data);
      updateUserLocal({ photo_url: null });
      toast.success('Profile photo removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const backTo = () => {
    if (authUser?.role === 'client') navigate('/portal/dashboard');
    else if (authUser?.role === 'cleaner') navigate('/cleaner/dashboard');
    else navigate('/admin/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  const role = account?.role || authUser?.role;
  const roleMeta = ROLE_META[role] || ROLE_META.client;
  const RoleIcon = roleMeta.icon;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="account-settings-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={backTo} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Account Settings</h1>
              <p className="text-xs text-zinc-500">Manage your profile and preferences</p>
            </div>
          </div>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl"
            data-testid="save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Identity Hero */}
        <Card className="border-zinc-100 overflow-hidden">
          <div className="bg-zinc-950 px-6 py-6 relative">
            <div className="absolute inset-0 opacity-10"
              style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #F56F05 0%, transparent 40%)' }} />
            <div className="relative flex items-start gap-5 flex-col sm:flex-row sm:items-center">
              <div className="relative">
                <UserAvatar user={{ ...account, full_name: form.full_name }} size="xl" testId="profile-avatar" />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#F56F05] hover:bg-[#e06505] text-white flex items-center justify-center shadow-lg shadow-orange-500/40 disabled:opacity-60"
                  title="Change photo"
                  data-testid="change-photo-btn"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-white truncate" data-testid="profile-name">{form.full_name || 'Your Name'}</h2>
                <p className="text-sm text-zinc-400 truncate" data-testid="profile-email">{account?.email}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge className={`${roleMeta.tint} ${roleMeta.accent} border-0 font-semibold uppercase tracking-wide text-[10px]`} data-testid="role-badge">
                    <RoleIcon className="h-3 w-3 mr-1" /> {roleMeta.label}
                  </Badge>
                  {account?.phone && <span className="text-xs text-zinc-400">{account.phone}</span>}
                </div>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleFile}
              data-testid="photo-input"
            />
            {account?.photo_url && (
              <button
                type="button"
                onClick={removePhoto}
                className="mt-4 text-xs text-zinc-400 hover:text-red-400 flex items-center gap-1"
                data-testid="remove-photo-btn"
              >
                <Trash2 className="h-3 w-3" /> Remove photo
              </button>
            )}
          </div>
        </Card>

        {/* Personal info */}
        <Card className="border-zinc-100">
          <CardHeader className="py-3 border-b border-zinc-100">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-[#F56F05]" /> Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold text-zinc-500">Full Name *</Label>
                <Input
                  value={form.full_name}
                  onChange={e => set('full_name', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200"
                  data-testid="input-name"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-zinc-500">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="(503) 555-1234"
                  className="mt-1 rounded-xl border-zinc-200"
                  data-testid="input-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold text-zinc-500 flex items-center gap-1">
                <Lock className="h-3 w-3" /> Email (contact support to change)
              </Label>
              <Input
                value={account?.email || ''}
                disabled
                className="mt-1 bg-zinc-50 rounded-xl border-zinc-200"
                data-testid="input-email"
              />
            </div>
          </CardContent>
        </Card>

        {/* Preferred Contact */}
        <Card className="border-zinc-100">
          <CardHeader className="py-3 border-b border-zinc-100">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-[#F56F05]" /> Preferred Contact Method
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-2">
              {CONTACT_METHODS.map(opt => {
                const active = form.preferred_contact === opt.id;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => set('preferred_contact', opt.id)}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      active ? 'border-[#F56F05] bg-orange-50' : 'border-zinc-100 hover:border-zinc-200'
                    }`}
                    data-testid={`contact-method-${opt.id}`}
                  >
                    <Icon className={`h-5 w-5 mx-auto mb-1 ${active ? 'text-[#F56F05]' : 'text-zinc-400'}`} />
                    <p className={`text-xs font-semibold ${active ? 'text-[#F56F05]' : 'text-zinc-700'}`}>{opt.label}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="border-zinc-100">
          <CardHeader className="py-3 border-b border-zinc-100">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#F56F05]" />
              {role === 'cleaner' ? 'Home Address' : role === 'client' ? 'Service Address' : 'Business Address'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div>
              <Label className="text-xs font-bold text-zinc-500">Street Address</Label>
              <Input
                value={form.address_line1}
                onChange={e => set('address_line1', e.target.value)}
                placeholder="123 Main St"
                className="mt-1 rounded-xl border-zinc-200"
                data-testid="input-address1"
              />
            </div>
            <div>
              <Label className="text-xs font-bold text-zinc-500">Apt / Suite</Label>
              <Input
                value={form.address_line2}
                onChange={e => set('address_line2', e.target.value)}
                placeholder="Suite 200"
                className="mt-1 rounded-xl border-zinc-200"
                data-testid="input-address2"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold text-zinc-500">City</Label>
                <Input
                  value={form.city}
                  onChange={e => set('city', e.target.value)}
                  placeholder="Portland"
                  className="mt-1 rounded-xl border-zinc-200"
                  data-testid="input-city"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-zinc-500">State</Label>
                <Input
                  value={form.state}
                  onChange={e => set('state', e.target.value.toUpperCase())}
                  maxLength={2}
                  placeholder="OR"
                  className="mt-1 rounded-xl border-zinc-200"
                  data-testid="input-state"
                />
              </div>
              <div>
                <Label className="text-xs font-bold text-zinc-500">ZIP</Label>
                <Input
                  value={form.zip}
                  onChange={e => set('zip', e.target.value)}
                  maxLength={5}
                  placeholder="97209"
                  className="mt-1 rounded-xl border-zinc-200"
                  data-testid="input-zip"
                />
              </div>
            </div>
            {role === 'client' && (
              <p className="text-[11px] text-zinc-400">
                Need multiple service addresses? Manage them in your{' '}
                <button type="button" onClick={() => navigate('/portal/profile')} className="text-[#F56F05] hover:underline">
                  client portal address book
                </button>.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Notes / Preferences */}
        <Card className="border-zinc-100">
          <CardHeader className="py-3 border-b border-zinc-100">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#F56F05]" /> Notes & Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Label className="text-xs font-bold text-zinc-500">
              Anything we should know? <span className="text-zinc-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder={role === 'client'
                ? 'e.g. Eco-friendly products only, gate code 1234, focus on kitchen and bathrooms...'
                : role === 'cleaner'
                ? 'Availability, certifications, transportation, languages spoken...'
                : 'Internal preferences or notes for the team...'}
              rows={4}
              className="mt-1 rounded-xl border-zinc-200"
              data-testid="input-notes"
            />
          </CardContent>
        </Card>

        {/* Save footer */}
        <div className="flex justify-end gap-3 pb-12">
          <Button variant="outline" onClick={backTo} className="rounded-xl" data-testid="cancel-btn">Cancel</Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl px-8"
            data-testid="save-btn-bottom"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
